import { useState, type ReactNode } from 'react';
import { ImageCarousel } from '../files';
import type { MediaView } from './MediaToggleBar';
import type { DmScanPhoto } from './types';
import { useScanPhotos } from './useScanPhotos';

export interface MonitoringMorphing {
    url: string | null;
    isLoading?: boolean;
}

function layerClass(isVisible: boolean): string {
    return `absolute inset-0 transition-opacity duration-200 ${
        isVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
    }`;
}

function MorphingLayer({ morphing }: { morphing: MonitoringMorphing }) {
    const [isVideoReady, setIsVideoReady] = useState(false);
    const url = morphing.url ?? '';

    if (morphing.isLoading) {
        return <div className="bg-gray-200 rounded-xl animate-pulse w-full h-full" />;
    }
    if (!url) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-600">
                No Morphing preview
            </div>
        );
    }

    return (
        <div className="relative w-full h-full bg-black rounded-lg overflow-hidden">
            {!isVideoReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
                    <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-white animate-spin" />
                </div>
            )}
            <video
                key={url}
                className="w-full h-full object-contain"
                controls
                preload="auto"
                playsInline
                webkit-playsinline="true"
                onCanPlay={() => setIsVideoReady(true)}
                onLoadedMetadata={() => setIsVideoReady(true)}
                onError={() => setIsVideoReady(true)}
            >
                <source src={url} type="video/mp4" />
                <source src={url} type="video/quicktime" />
                Oops! It looks like your browser isn't feeling up to playing this video.
            </video>
        </div>
    );
}

export interface MonitoringMediaProps {
    mediaView: MediaView;
    photos: DmScanPhoto[] | null | undefined;
    photosLoading?: boolean;
    resetKey?: number;
    morphing?: MonitoringMorphing | null;
    sidePanel?: ReactNode;
}

function MonitoringMedia({
    mediaView,
    photos,
    photosLoading = false,
    resetKey,
    morphing = null,
    sidePanel
}: MonitoringMediaProps) {
    const { images, labels, thumbnails } = useScanPhotos(photos);

    return (
        <div
            className={
                sidePanel
                    ? 'flex-1 min-h-0 grid grid-cols-[2fr_1fr] gap-4'
                    : 'flex-1 min-h-0 flex flex-col'
            }
        >
            <div className={`${sidePanel ? '' : 'flex-1'} min-h-0 relative`}>
                {/* Both layers stay mounted so toggling views never reloads the media. */}
                <div className={layerClass(mediaView === 'photos')}>
                    {photosLoading ? (
                        <div className="bg-gray-200 rounded-xl animate-pulse w-full h-full" />
                    ) : (
                        <ImageCarousel
                            images={images}
                            labels={labels}
                            thumbnails={thumbnails}
                            resetKey={resetKey}
                        />
                    )}
                </div>
                {morphing && (
                    <div className={layerClass(mediaView === 'morphing')}>
                        <MorphingLayer morphing={morphing} />
                    </div>
                )}
            </div>
            {sidePanel && <div className="min-h-0 flex flex-col gap-3">{sidePanel}</div>}
        </div>
    );
}

export { MonitoringMedia };
